import CardLayout from '@/components/common/CardLayout';
import { FlexRow } from '@/components/common/Flex';
import Modal from '@/components/common/modals/Modal';
import GoalIcon from '@/components/goals/GoalIcon';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { Spinner, Text, VStack } from '@chakra-ui/react';
import {
    ExternalFinancialEntity,
    isActiveTransactionSchedule,
} from '@common/financialEntities';
import { Goal } from '@common/goals';
import { TransactionScheduleFieldsFragment } from '@common/graphql/generated';
import {
    useDeleteFinancialEntity,
    useGoalsFundedByAccount,
    useIsActiveFundingAccount,
} from '@common/react/financialEntities';
import { getAmountRecurrenceStr } from '@common/react/goals';
import { FlexCol } from '../common/Flex';

export interface DeleteModalProps {
    financialEntity: ExternalFinancialEntity;
    onComplete: VoidFunction;
}

export const showDeleteModal = (props: DeleteModalProps) =>
    ModalManager.show(DeleteModal, props);

const DeleteModal = ModalManager.create<DeleteModalProps>(
    ({ onComplete, financialEntity }) => {
        useTrackModalShown('Delete Financial Entity');

        const modal = useModal();
        const isActiveFundingAccount =
            useIsActiveFundingAccount(financialEntity);
        const fundingGoals = useGoalsFundedByAccount(financialEntity);
        const data = useDeleteFinancialEntity({
            financialEntity,
            onComplete: () => {
                modal.remove();
                onComplete();
            },
        });

        if (data.isLoading) return <Spinner />;

        if (data.isAlreadyDeleted) {
            modal.remove();
            return <></>;
        }

        const { title, description, deleteFinancialEntity, isSubmitting } =
            data;

        return (
            <Modal
                size={isActiveFundingAccount ? 'md' : 'xs'}
                isOpen={modal.visible}
                onClose={modal.hide}
            >
                <CardLayout
                    title={title}
                    primaryButtonText="Delete"
                    onPrimaryButton={deleteFinancialEntity}
                    isPrimaryButtonLoading={isSubmitting}
                    onSecondaryButton={modal.remove}
                    onClickPrimaryEvent="Delete Financial Entity Confirm Button Clicked"
                    onClickSecondaryEvent="Cancel Delete Financial Entity Button Clicked"
                >
                    <FlexCol>
                        <Text fontSize="md" color="textMuted">
                            {description}
                        </Text>
                        {isActiveFundingAccount && (
                            <VStack spacing={0} align="stretch">
                                {fundingGoals.map(
                                    ({ goal, transactionSchedules }) => (
                                        <FundingGoal
                                            key={goal.id}
                                            goal={goal}
                                            transactionSchedules={
                                                transactionSchedules
                                            }
                                        />
                                    )
                                )}
                            </VStack>
                        )}
                    </FlexCol>
                </CardLayout>
            </Modal>
        );
    }
);

const FundingGoal = ({
    goal,
    transactionSchedules,
}: {
    goal: Goal;
    transactionSchedules: TransactionScheduleFieldsFragment[];
}) => {
    return (
        <FlexRow
            mt={6}
            key={goal.id}
            h={16}
            align="center"
            justify="space-between"
            gap={4}
            alignItems="flex-start"
        >
            <FlexRow alignItems="center" flexGrow={1}>
                <GoalIcon goal={goal} />
                <FlexCol justifyContent="center" ml={4} maxW={40} minW={0}>
                    <Text
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                    >
                        {goal.name}
                    </Text>
                </FlexCol>
            </FlexRow>
            <FlexCol textAlign="right" justify="center">
                {transactionSchedules
                    .filter(isActiveTransactionSchedule)
                    .map((transactionSchedule) => (
                        <Text
                            key={transactionSchedule.id}
                            fontSize="md"
                            fontWeight="medium"
                        >
                            {getAmountRecurrenceStr({
                                amount: transactionSchedule.amount,
                                recurrence: transactionSchedule.recurrence,
                            })}
                        </Text>
                    ))}
            </FlexCol>
        </FlexRow>
    );
};

export default DeleteModal;
